import {
  Avatar,
  AvatarGroup,
  Box,
  ButtonBase,
  Chip,
  Stack,
  Typography
} from "@mui/material";
import { ArrowForwardRounded, PersonSearchRounded, StarRounded, WorkRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { HERO_PROFESSIONALS } from "../content";
import { PageContainer } from "./SectionLayout";

function HeroActionCard({ description, icon: Icon, inverted = false, onClick, title }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        alignItems: "center",
        bgcolor: inverted ? "background.paper" : "#5d3fd3",
        border: "1px solid",
        borderColor: inverted ? "divider" : "#5d3fd3",
        borderRadius: 2,
        boxShadow: inverted
          ? "0 14px 35px rgba(55, 36, 111, 0.08)"
          : "0 18px 38px rgba(69, 30, 187, 0.24)",
        color: inverted ? "text.primary" : "common.white",
        display: "flex",
        justifyContent: "space-between",
        minHeight: 126,
        p: 3,
        textAlign: "left",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        width: "100%",
        "&:hover": {
          boxShadow: inverted
            ? "0 20px 45px rgba(55, 36, 111, 0.13)"
            : "0 22px 44px rgba(69, 30, 187, 0.3)",
          transform: "translateY(-3px)"
        },
        "&:hover .action-arrow": { transform: "translateX(4px)" }
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Icon sx={{ color: inverted ? "secondary.main" : "inherit", fontSize: 26, mb: 1 }} />
        <Typography
          sx={{ fontSize: 18, fontWeight: 400, lineHeight: 1.35 }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: inverted ? "#484554" : "rgba(255,255,255,0.82)",
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.5,
            mt: 0.4
          }}
        >
          {description}
        </Typography>
      </Box>
      <ArrowForwardRounded
        className="action-arrow"
        sx={{
          color: inverted ? "primary.main" : "inherit",
          flexShrink: 0,
          fontSize: 28,
          ml: 1,
          transition: "transform 180ms ease"
        }}
      />
    </ButtonBase>
  );
}

function MetricCard({ label, value, change }) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: alpha("#c9c4d7", 0.65),
        borderRadius: 1.5,
        p: { xs: 1.5, sm: 2 },
        boxShadow: "0 8px 24px rgba(93, 63, 211, 0.06)"
      }}
    >
      <Typography color="text.secondary" sx={{ fontSize: 12 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
        {value}
      </Typography>
      <Typography
        color="secondary.main"
        sx={{ fontSize: 10, fontWeight: 700, mt: 0.5 }}
      >
        {change}
      </Typography>
    </Box>
  );
}

function DashboardPreview() {
  const bars = [30, 50, 80, 60, 95];
  const barColors = [
    alpha("#451ebb", 0.2),
    alpha("#451ebb", 0.4),
    alpha("#451ebb", 0.6),
    "#451ebb",
    "#5d3fd3"
  ];

  return (
    <Box
      role="img"
      aria-label="Recruitment dashboard showing jobs, applicants and an applications chart"
      sx={{ position: "relative" }}
    >
      <Box
        sx={{
          bgcolor: "landing.surfaceLow",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: { xs: 2.5, md: 3 },
          boxShadow: "0 28px 70px rgba(77, 50, 154, 0.15)",
          p: { xs: 1.5, sm: 3 },
          position: "relative",
          zIndex: 1
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography
            color="primary.main"
            sx={{ fontSize: 14, fontWeight: 500, letterSpacing: "0.12em" }}
          >
            DASHBOARD
          </Typography>
          <Stack direction="row" spacing={0.75}>
            {["#ba1a1a", "#fbbf24", "#00a875"].map((color) => (
              <Box key={color} sx={{ bgcolor: color, borderRadius: "50%", height: 7, width: 7 }} />
            ))}
          </Stack>
        </Stack>

        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          <MetricCard label="Total Jobs" value="24" change="+12% from last month" />
          <MetricCard label="Total Applicants" value="356" change="+18% from last month" />
        </Box>

        <Box
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: alpha("#c9c4d7", 0.65),
            borderRadius: 1.5,
            boxShadow: "0 8px 24px rgba(93, 63, 211, 0.06)",
            mt: 3,
            p: { xs: 1.5, sm: 2 }
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Applications Overview</Typography>
            <Chip label="This Month" size="small" sx={{ fontSize: 10, height: 22 }} />
          </Stack>
          <Stack
            direction="row"
            alignItems="flex-end"
            spacing={{ xs: 0.75, sm: 1 }}
            sx={{ height: 128, mt: 2 }}
          >
            {bars.map((height, index) => (
              <Box
                key={height}
                sx={{
                  bgcolor: barColors[index],
                  borderRadius: "5px 5px 0 0",
                  height: `${height}%`,
                  width: "100%"
                }}
              />
            ))}
          </Stack>
        </Box>
      </Box>
      <Box
        sx={{
          bgcolor: alpha("#5d3fd3", 0.18),
          borderRadius: "50%",
          filter: "blur(70px)",
          height: 260,
          position: "absolute",
          right: -70,
          top: -70,
          width: 260
        }}
      />
      <Box
        sx={{
          bgcolor: alpha("#00a875", 0.14),
          borderRadius: "50%",
          bottom: -60,
          filter: "blur(70px)",
          height: 220,
          left: -50,
          position: "absolute",
          width: 220
        }}
      />
    </Box>
  );
}

export default function HeroSection({ onFindJobs, onStartHiring }) {
  return (
    <Box
      component="header"
      sx={{
        overflow: "hidden",
        pb: { xs: 8, sm: 9, md: 10, lg: 8 },
        position: "relative",
        pt: { xs: 13, sm: 14, md: 15, lg: 17 }
      }}
    >
      <PageContainer>
        <Box
          sx={{
            alignItems: "center",
            display: "grid",
            gap: { xs: 6, sm: 7, md: 6, lg: 8 },
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) minmax(400px, 0.95fr)",
              lg: "repeat(2, minmax(0, 1fr))"
            }
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                fontSize: {
                  xs: "clamp(2.125rem, calc(10vw + 0.25rem), 3rem)",
                  sm: 48,
                  md: 46,
                  lg: 56
                },
                fontWeight: 400,
                letterSpacing: "-0.02em",
                lineHeight: 1.1
              }}
            >
              The Complete
              <br />
              <Box component="span" sx={{ color: "primary.main" }}>
                AI Recruitment
              </Box>{" "}
              Platform
            </Typography>
            <Typography
              sx={{
                color: "#484554",
                fontSize: { xs: 15, sm: 16, md: 16, lg: 18 },
                lineHeight: 1.6,
                maxWidth: 620,
                mt: { xs: 2.5, sm: 3 }
              }}
            >
              Whether you&apos;re hiring top talent or looking for your next opportunity, NextHire
              connects people and companies through intelligent technology.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: { xs: 2, sm: 3 },
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "1fr",
                  lg: "repeat(2, minmax(0, 1fr))"
                },
                mt: { xs: 4, sm: 4.5 }
              }}
            >
              <HeroActionCard
                title="I'm Hiring"
                description="Post jobs & hire talent"
                icon={WorkRounded}
                onClick={onStartHiring}
              />
              <HeroActionCard
                inverted
                title="I'm Looking for a Job"
                description="Find jobs & build career"
                icon={PersonSearchRounded}
                onClick={onFindJobs}
              />
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              spacing={{ xs: 1.5, sm: 2.5 }}
              mt={{ xs: 4, sm: 6 }}
            >
              <AvatarGroup
                max={3}
                sx={{
                  "& .MuiAvatar-root": {
                    border: "2px solid #ffffff",
                    height: 38,
                    width: 38
                  }
                }}
              >
                {HERO_PROFESSIONALS.map((professional) => (
                  <Avatar
                    key={professional.alt}
                    src={professional.image}
                    alt={professional.alt}
                  />
                ))}
              </AvatarGroup>
              <Box>
                <Stack direction="row">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <StarRounded key={index} sx={{ color: "#f4b740", fontSize: 16 }} />
                  ))}
                </Stack>
                <Typography color="text.secondary" sx={{ fontSize: 12, fontWeight: 500 }}>
                  Trusted by companies and candidates
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              maxWidth: { xs: 680, md: "none" },
              mx: { xs: "auto", md: 0 },
              transform: { xs: "none", md: "scale(1.04)", lg: "scale(1.1)" },
              transformOrigin: "center center",
              width: "100%"
            }}
          >
            <DashboardPreview />
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
